import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { STEPPER_BUTTON_STATUS } from '../../shared/constants/stepper-button-status';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { BuyerSurveyResponse } from '../../shared/interfaces/survey.interface';
import { SurveyMetaDataService } from '../../shared/services/survey-meta-data/survey-meta-data.service';
import { SurveyPayload } from '../../shared/interfaces/survey-meta-data.interface';
import { SurveyPayloadService } from '../../create-surveys/survey-payload.service';
import {
  QuotaPayload,
  Criteria,
} from '../../shared/interfaces/quota.interface';
import { QUALIFICATION_CODES } from '../../shared/constants/qualification-code';
import { ChildrenLabelMapper } from 'purespectrum-lib';
@Component({
  selector: 'ps-ui-quota-mapper',
  templateUrl: './quota-mapper.component.html',
  styleUrls: ['./quota-mapper.component.css'],
})
export class QuotaMapperComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  public surveyId!: number;
  public surveyDetails!: BuyerSurveyResponse;
  public quotas!: QuotaPayload[];
  public footerButtonStatus: number = STEPPER_BUTTON_STATUS.MAP_QUOTAS;
  public isQuotaMapRemainder: boolean = false;
  public isTrasmittingQuotaMap: boolean = false;
  public surveyPayload: SurveyPayload = {} as SurveyPayload;
  public isConfirmationChecked: boolean = false;

  constructor(
    private _route: ActivatedRoute,
    private _buyerApiService: BuyerApiService,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _surveyPayloadService: SurveyPayloadService
  ) {}

  ngOnInit(): void {
    this._route.params.subscribe((params) => {
      this.surveyId = params['id'];
    });
    if (this.surveyId) {
      this._getSurveyDetails();
    }
  }

  private _getSurveyDetails() {
    const getSurveyDetails$ = this._buyerApiService
      .getSurveyById(this.surveyId)
      .subscribe((resp) => {
        this.surveyDetails = resp;
        this.surveyPayload = this._surveyPayloadService.mapSurveyDetails(resp);
        this.quotas = this.surveyPayload.quotas || [];
        this._updatePayload();
      });
    this._subscriptions.add(getSurveyDetails$);
  }

  public getQuotaDescription(quota: QuotaPayload) {
    let description: string[] = [];
    quota.criteria.forEach((eachCriteria: Criteria) => {
      if (eachCriteria?.range_sets) {
        eachCriteria.range_sets.forEach((eachRangeSet) => {
          description.push(`${eachRangeSet.from}-${eachRangeSet.to}`);
        });
      } else {
        description = description.concat(
          this._getQuotaDescriptionForConditionCodes(eachCriteria)
        );
      }
    });
    return description.join(',');
  }

  private _getQuotaDescriptionForConditionCodes(criteria: Criteria) {
    if (
      criteria.qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER &&
      criteria.condition_codes?.length
    ) {
      const mapper = new ChildrenLabelMapper(
        criteria.condition_codes?.map((code) => Number(code)),
        criteria.child_age_unit
      );
      return mapper.label();
    } else {
      return criteria.condition_names || '';
    }
  }

  public setQuotaMapRemainder() {
    this.isQuotaMapRemainder = !this.isQuotaMapRemainder;
    this.isTrasmittingQuotaMap = false;
  }

  public setTrasmittingQuotaMap() {
    this.isTrasmittingQuotaMap = !this.isTrasmittingQuotaMap;
    this.isQuotaMapRemainder = false;
  }

  public resetQlcAttributes() {
    this.surveyDetails.qlc.multiple_quotas.enable =
      !this.surveyDetails.qlc.multiple_quotas.enable;
    if (
      this.surveyDetails.qlc &&
      !this.surveyDetails.qlc.multiple_quotas.enable
    ) {
      this.surveyDetails.qlc.multiple_quotas.count_by_quota = false;
      this.surveyDetails.qlc.multiple_quotas.compensate_by_quota = false;
    }
    this._updatePayload();
  }

  public setCompensateByQuota() {
    if (
      this.surveyDetails.qlc &&
      !this.surveyDetails.qlc.multiple_quotas.count_by_quota
    ) {
      this.surveyDetails.qlc.multiple_quotas.compensate_by_quota = false;
    }
    this._updatePayload();
  }

  public changeBuyerQuotaId() {
    this._updatePayload();
  }

  private _updatePayload() {
    this._resetQuotaTable();
    this.surveyPayload = {
      ...this.surveyPayload,
      ...this.quotas,
      ...this.surveyDetails.qlc,
    };
  }

  private _resetQuotaTable() {
    const quotas = this.quotas;
    let charCountExceedQuotas: string[] = [];
    let duplicatedBuyerQuotaIds: string[] = [];
    let groupedQuotas = quotas?.reduce(function (a: any, e: any) {
      let estKey = e['buyer_quota_id'];
      (a[estKey] ? a[estKey] : (a[estKey] = null || [])).push(e);
      return a;
    }, {});
    let buyerQuotaIds = Object.keys(groupedQuotas);
    Object.keys(groupedQuotas).forEach((groupedQuotaKey, i) => {
      if (groupedQuotas[groupedQuotaKey].length > 1) {
        duplicatedBuyerQuotaIds.push(groupedQuotaKey);
      }
    });

    let originalBuyerQuotaIds: string[] = [];
    quotas?.forEach((quota: QuotaPayload, index: number) => {
      quotas[index].uiIsDuplicate = false;
      quotas[index].charCountExceeded = false;
      if (
        quota.buyer_quota_id &&
        buyerQuotaIds.includes(quota.buyer_quota_id) &&
        groupedQuotas[quota.buyer_quota_id].length > 1
      ) {
        if (
          quota.buyer_quota_id &&
          originalBuyerQuotaIds.includes(quota.buyer_quota_id)
        ) {
          quotas[index].uiIsDuplicate = false;
          originalBuyerQuotaIds.push(quota.buyer_quota_id);
        } else {
          quotas[index].uiIsDuplicate = true;
        }
      }
      if (quota.buyer_quota_id && quota.buyer_quota_id.length > 15) {
        charCountExceedQuotas.push(quota.buyer_quota_id);
        quotas[index].charCountExceeded = true;
      }
    });
    this.quotas = quotas;
    this._surveyMetaDataService.setQuotaMapperValidateData({
      charCountExceedQuotas,
      duplicatedBuyerQuotaIds,
    });
  }

  formatValidation(e: KeyboardEvent) {
    const isInvalid = !/^[a-z|0-9|\-]+$/gi.test(e.key);
    if (isInvalid) {
      e.preventDefault();
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
